import BulkStatusChangeDialog from '@/app/features/reports/components/bulk-change-dialog';
import useReportViewerContext, { useReportViewContext } from '@/app/features/reports/components/report-viewer-context';
import { useReportSelectionContext } from '@/app/features/reports/contexts/selection-context';
import { TestStatus } from '@app.raytd.com/common/types/test-form';
import { ReportTestItem } from '@/app/features/reports/utils/entities';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { capitalize, cn } from '@/lib/utils';
import { useBulkUpdateTestItemsMutation } from '@app.raytd.com/store';
import { Check, ChevronDown, ListOrderedIcon, X, ArchiveX, ArchiveRestore } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { BulkAction, BulkActionType, TestInfo, IneligibleTestInfo } from '@/app/features/reports/types/bulk-actions';
import { FastFillIcon } from '@/components/raytd/icons';
import { getStatusDisplayName } from '@/app/features/reports/utils/getStatusDisplayName';
import isTestReadyForStatus from '@/app/features/reports/utils/isTestReadyForStatus';

const friendlyExclusionReason = {
    'included': 'Included',
    'manuallyexcluded': 'Manually Excluded',
    'status': 'Status',
    'unmatchedsegments': 'Fast Fills not accepted',
    'missingdata': 'Missing Data'
} as const;

type ExclusionReasonKey = keyof typeof friendlyExclusionReason;

interface BulkEditDropdownProps {
    filteredData: ReportTestItem[];
}

const BulkEditDropdown = ({
    filteredData,
}: BulkEditDropdownProps) => {
    const { selectedItems, setSelectedItems, bulkEditMode, setBulkEditMode } = useReportSelectionContext();

    const [isBulkActionDialogOpen, setIsBulkActionDialogOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentBulkAction, setCurrentBulkAction] = useState<BulkAction | null>(null);
    const [eligibleTests, setEligibleTests] = useState<TestInfo[]>([]);
    const [ineligibleTests, setIneligibleTests] = useState<IneligibleTestInfo[]>([]);

    const { reportId, setBulkUpdatingTestIds } = useReportViewerContext();
    const { testSuites } = useReportViewContext();
    const [bulkUpdateTestItems] = useBulkUpdateTestItemsMutation();

    const isInReportContext = reportId !== null;

    // --- Eligibility Check Functions ---

    const checkStatusChangeEligibility = useCallback((test: ReportTestItem, status: TestStatus, notReadyForFinal: Set<number> = new Set()): { eligible: boolean; reason?: string } => {
        const hasScore = test.ratingType !== 'generic' && test.score !== null && test.score !== undefined && test.score >= 0;
        const excludedByStatus = test.exclusionReason === 'status';
        // const excludedByFastFill = test.exclusionReason === 'unmatchedsegments';
        const hasMissingData = test.hasMissingData || test.exclusionReason === 'missingdata';

        const friendlyReason = friendlyExclusionReason[test.exclusionReason as ExclusionReasonKey] ?? 'Unknown';

        if (status === 'published') {
            // Check for missing data explicitly - prevent setting to FINAL if data is incomplete
            if (hasMissingData) {
                return { eligible: false, reason: 'Test has missing data' };
            }

            // `hasMissingData` is blind to Draft tests (see isTestReadyForStatus).
            if (notReadyForFinal.has(test.assessmentTestId)) {
                return { eligible: false, reason: 'Required fields are missing' };
            }

            const canChangeFinal = true;
            console.log('canChangeFinal', {
                canChangeFinal,
                hasScore,
                excludedByStatus,
                friendlyReason,
                status,
                test
            });
            return { eligible: canChangeFinal, reason: canChangeFinal ? undefined : friendlyReason };
        }

        if (status === 'qa') {
            // QA should allow tests without scores (e.g., generic tests) and incomplete data
            // Only check that the test isn't manually excluded or has other blocking reasons
            const canChangeQA = true;
            console.log('canChangeQA', {
                canChangeQA,
                excludedByStatus,
                friendlyReason,
                status,
                test
            });
            return { eligible: canChangeQA, reason: canChangeQA ? undefined : friendlyReason };
        }

        return { eligible: true }; // Assume other statuses are always eligible
    }, []);

    const checkAcceptFastFillEligibility = useCallback((test: ReportTestItem): { eligible: boolean; reason?: string } => {
        // const isFastFill = test.hasUnmatchedSegments;
        const isFastFill = true;
        return { eligible: isFastFill, reason: isFastFill ? undefined : 'No fast fill errors' };
    }, []);

    // New eligibility check for excluding tests
    const checkExcludeEligibility = useCallback((test: ReportTestItem): { eligible: boolean; reason?: string } => {
        // const isEligible = test.switches?.include === true;
        // const reason = isEligible ? undefined : 'No action required';
        const isEligible = true;
        const reason = undefined;
        return { eligible: isEligible, reason };
    }, []);

    // New eligibility check for including tests
    const checkIncludeEligibility = useCallback((test: ReportTestItem): { eligible: boolean; reason?: string } => {
        // const isCurrentlyExcluded = test.switches?.include === false;
        // const hasExclusionReason = test.exclusionReason === 'manuallyexcluded';
        // const isEligible = isCurrentlyExcluded || hasExclusionReason;
        // const reason = isEligible ? undefined : friendlyExclusionReason[test.exclusionReason as ExclusionReasonKey];
        const isEligible = true;
        const reason = undefined;

        return { eligible: isEligible, reason };
    }, []);

    // --- Action Trigger Functions ---

    const prepareBulkAction = useCallback((
        actionType: BulkActionType,
        eligibilityCheck: (test: ReportTestItem) => { eligible: boolean; reason?: string },
        params?: BulkAction['params']
    ) => {
        const selectedTests = filteredData.filter(test => selectedItems.has(test.assessmentTestId.toString()));
        const initialAccumulator: { eligible: TestInfo[]; ineligible: IneligibleTestInfo[] } = { eligible: [], ineligible: [] };

        const { eligible, ineligible } = selectedTests.reduce((acc, test) => {
            const { eligible: isEligible, reason } = eligibilityCheck(test);
            const testInfo = {
                id: test.assessmentTestId.toString(),
                title: `Test ${test.assessmentTestId}`
            };

            if (isEligible) {
                acc.eligible.push(testInfo);
            } else {
                acc.ineligible.push({ ...testInfo, reason: reason ?? 'Unknown eligibility reason' });
            }
            return acc; // Return accumulator
        }, initialAccumulator); // Provide initial value

        setEligibleTests(eligible);
        setIneligibleTests(ineligible);
        setCurrentBulkAction({ type: actionType, params });
        setIsBulkActionDialogOpen(true);
    }, [filteredData, selectedItems]);

    const handleTriggerStatusChange = useCallback(async (status: TestStatus) => {
        const notReadyForFinal = new Set<number>();
        if (status === 'published') {
            const selectedTests = filteredData.filter(test => selectedItems.has(test.assessmentTestId.toString()));
            const ready = await Promise.all(selectedTests.map(test => isTestReadyForStatus(test, testSuites, status)));
            selectedTests.forEach((test, i) => {
                if (!ready[i]) notReadyForFinal.add(test.assessmentTestId);
            });
        }
        prepareBulkAction('status', (test) => checkStatusChangeEligibility(test, status, notReadyForFinal), { status });
    }, [prepareBulkAction, checkStatusChangeEligibility, filteredData, selectedItems, testSuites]);

    const handleTriggerAcceptFastFill = useCallback(() => {
        // This function triggers the preparation for the 'acceptFastFill' action
        prepareBulkAction('acceptFastFill', checkAcceptFastFillEligibility);
    }, [prepareBulkAction, checkAcceptFastFillEligibility]);

    // New trigger function for excluding tests
    const handleTriggerExclude = useCallback(() => {
        prepareBulkAction('exclude', checkExcludeEligibility);
    }, [prepareBulkAction, checkExcludeEligibility]);

    // New trigger function for including tests
    const handleTriggerInclude = useCallback(() => {
        prepareBulkAction('include', checkIncludeEligibility);
    }, [prepareBulkAction, checkIncludeEligibility]);

    // --- Confirmation Handler ---

    const handleBulkActionConfirm = useCallback(async () => {
        if (!currentBulkAction || eligibleTests.length === 0) return;

        const { type, params } = currentBulkAction;
        const testIds = eligibleTests.map(test => parseInt(test.id, 10));

        // Validate reportId for include/exclude actions
        if ((type === 'include' || type === 'exclude') && !reportId) {
            toast.error('Cannot include/exclude tests: Report context is required');
            setIsBulkActionDialogOpen(false);
            return;
        }

        // Set processing state and update context with test IDs being updated
        setIsProcessing(true);
        setBulkUpdatingTestIds?.(new Set(testIds));

        let promise: Promise<any>;
        let actionDescription = '';

        try {
            switch (type) {
                case 'status':
                    if (!params?.status) throw new Error("Status parameter missing");
                    const apiStatus = capitalize(params.status) as 'Draft' | 'Qa' | 'Published' | 'Archived';
                    const displayStatus = getStatusDisplayName(params.status);
                    actionDescription = `change status to ${displayStatus}`;
                    promise = bulkUpdateTestItems({
                        reportId: reportId,
                        testItems: testIds,
                        action: 'status',
                        status: apiStatus
                    }).unwrap();
                    break;
                case 'acceptFastFill':
                    actionDescription = 'accept fast fill';
                    promise = bulkUpdateTestItems({
                        reportId: reportId,
                        testItems: testIds,
                        action: 'fastfill'
                    }).unwrap();
                    break;
                case 'exclude':
                    actionDescription = 'exclude tests';
                    promise = bulkUpdateTestItems({
                        reportId: reportId,
                        testItems: testIds,
                        action: 'exclude',
                    }).unwrap();
                    break;
                case 'include':
                    actionDescription = 'include tests';
                    promise = bulkUpdateTestItems({
                        reportId: reportId,
                        testItems: testIds,
                        action: 'include',
                    }).unwrap();
                    break;
                default:
                    const _exhaustiveCheck: never = type;
                    throw new Error(`Unsupported bulk action type: ${_exhaustiveCheck}`);
            }

            const result = await promise; // Array of { id, status, success, error? }

            // Parse individual results
            const successful = result.filter(item => item.success === true);
            const failed = result.filter(item => item.success === false);

            // Display appropriate messages based on results
            if (failed.length === 0) {
                toast.success(`Successfully ${actionDescription} for ${successful.length} test${successful.length > 1 ? 's' : ''}.`);
            } else if (successful.length === 0) {
                const uniqueErrors = [...new Set(failed.map(item => item.error || 'Unknown error'))];
                toast.error(`Failed to ${actionDescription}: ${uniqueErrors.join(', ')}`);
            } else {
                toast.warning(`Partial success: ${successful.length} succeeded, ${failed.length} failed`);
                const uniqueErrors = [...new Set(failed.map(item => item.error || 'Unknown error'))];
                uniqueErrors.forEach(error => toast.error(String(error)));
            }

            if (ineligibleTests.length > 0) {
                toast.warning(`${ineligibleTests.length} tests were ineligible and not processed.`);
            }

        } catch (error: any) {
            console.error(`Failed to ${actionDescription}:`, error);

            // Handle specific HTTP error states
            if (error.status === 422) {
                const validationErrors = error.data?.errors || {};
                const messages = Object.values(validationErrors).flat().join(', ');
                toast.error(`Validation error: ${messages}`);
            } else if (error.status === 404) {
                toast.error(error.data?.error || 'Draft revision not found for this report.');
            } else {
                const errorMessage = error.data?.error || error.message || String(error);
                toast.error(`Failed to ${actionDescription}. ${errorMessage}`);
            }
        } finally {
            // Clear processing state and bulk updating IDs
            setIsProcessing(false);
            setBulkUpdatingTestIds?.(new Set());
            // Close dialog and reset action state (keep selections active for additional bulk actions)
            setIsBulkActionDialogOpen(false);
            setCurrentBulkAction(null);
        }
    }, [
        currentBulkAction,
        eligibleTests,
        ineligibleTests.length,
        bulkUpdateTestItems,
        reportId,
        setBulkUpdatingTestIds,
    ]);

    // --- Rendering ---

    if (!bulkEditMode || selectedItems.size === 0) {
        return (
            <Button
                variant='outlinelight'
                size='default'
                onClick={() => setBulkEditMode(prev => !prev)}
                className={cn(
                    bulkEditMode && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                )}
            >
                <ListOrderedIcon className="h-4 w-4 mr-1" />
                Bulk Edit
            </Button>
        );
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant='outlinelight'
                        size='default'
                        className={cn(
                            "bg-primary text-primary-foreground hover:bg-primary/90"
                        )}
                    >
                        <ListOrderedIcon className="h-4 w-4 mr-1" />
                        {`(${selectedItems.size}) Selected`}
                        <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">

                    <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                    <DropdownMenuGroup>
                        <DropdownMenuItem onClick={() => handleTriggerStatusChange('archived')} className="pl-6">
                            Archived
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleTriggerStatusChange('draft')} className="pl-6">
                            Draft
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleTriggerStatusChange('qa')} className="pl-6">
                            QA
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleTriggerStatusChange('published')} className="pl-6">
                            Final
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </DropdownMenuGroup>

                    <DropdownMenuGroup hidden={!isInReportContext}>
                        <DropdownMenuItem onClick={handleTriggerExclude} className="">
                            Exclude Tests
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleTriggerInclude} className="">
                            Include Tests
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                    </DropdownMenuGroup>

                    <DropdownMenuGroup>
                        <DropdownMenuItem onClick={handleTriggerAcceptFastFill}>
                            <FastFillIcon className="h-4 w-4 mr-2" />
                            Accept Fast Fill
                        </DropdownMenuItem>
                    </DropdownMenuGroup>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={() => {
                            setSelectedItems(new Set());
                        }}
                        className="text-destructive"
                    >
                        <X className="h-4 w-4 mr-2" />
                        Clear Selection
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Pass the action object to the dialog */}
            <BulkStatusChangeDialog
                isOpen={isBulkActionDialogOpen}
                onClose={() => setIsBulkActionDialogOpen(false)}
                eligibleTests={eligibleTests}
                ineligibleTests={ineligibleTests}
                action={currentBulkAction} // Pass the whole action object
                onConfirm={handleBulkActionConfirm}
                isProcessing={isProcessing}
            />
        </>
    );
};

export default BulkEditDropdown;